/**
 * A splash screen (ORDR-164). There is no approved canvas for it, so it is
 * drawn in the language of `Main.dc.html`: the brand header above it (from
 * `BrandedSurface`), Source Sans 3 where the device has it, a 26px heading,
 * an optional image, and ONE action in the brand's primary colour at 52px.
 *
 * TEXT, NEVER HTML. `title` and `message` are React text children, so an
 * `<img onerror>` in either renders as the characters it is made of.
 * `whitespace-pre-line` keeps the author's line breaks without parsing
 * anything. The image is decorative (`alt=""`): its words, if any, are in the
 * title and message beside it.
 */

import type { ReactElement } from "react";
import type { SplashMessage } from "./splashApi.js";

export interface SplashProps {
  readonly messages: readonly SplashMessage[];
  /** "Continue" before the chooser, "Start your order" before the menu. */
  readonly actionLabel: string;
  readonly onContinue: () => void;
}

export const SURFACE_FONT = "'Source Sans 3', var(--font-body), system-ui, sans-serif";

export function Splash({ messages, actionLabel, onContinue }: SplashProps): ReactElement {
  return (
    <section
      data-testid="splash"
      aria-label="Welcome"
      className="flex min-h-[70dvh] flex-col gap-5 p-5"
      style={{ fontFamily: SURFACE_FONT }}
    >
      {messages.map((message) => (
        <article key={`${message.level}:${message.title}`} className="flex flex-col gap-3">
          {message.imageUrl !== null && (
            <img
              data-testid="splash-image"
              src={message.imageUrl}
              alt=""
              referrerPolicy="no-referrer"
              className="aspect-[16/9] w-full rounded-xl bg-[var(--color-neutral-100)] object-cover"
            />
          )}
          <h1 className="m-0 text-[26px] font-bold leading-tight">{message.title}</h1>
          <p className="m-0 whitespace-pre-line text-[17px] text-[var(--color-neutral-700)]">{message.message}</p>
        </article>
      ))}
      <span className="grow" />
      <button
        type="button"
        onClick={onContinue}
        className="min-h-[52px] w-full rounded-xl bg-[var(--color-primary-700)] px-5 text-[17px] font-bold text-[var(--color-neutral-50)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary-500)]"
      >
        {actionLabel}
      </button>
    </section>
  );
}
